import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Mail, Lock, Phone, User, Tag, ArrowLeft, Rocket, CheckCircle, Clock, Shield, Package } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const TrialRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    empresa_nombre: '',
    contacto_nombre: '',
    email: '',
    password: '',
    telefono: '',
    prefijo_factura: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.empresa_nombre || !form.email || !form.password || !form.telefono || !form.contacto_nombre) {
      toast.error('Complete todos los campos obligatorios');
      return;
    }
    if (form.password.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/register-trial', form);
      setSuccess(res.data);
      toast.success('Empresa creada exitosamente!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la empresa');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl p-8 text-center" data-testid="trial-success">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Empresa Creada!</h1>
          <p className="text-lg text-green-600 font-semibold mb-4">{success.empresa}</p>
          
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4 text-left">
            <p className="font-bold text-blue-800 mb-2 flex items-center gap-2"><Clock className="h-4 w-4" /> Periodo de Prueba</p>
            <p className="text-blue-700">Tiene <span className="font-bold text-xl">{success.dias_restantes} dias</span> de prueba gratuita</p>
            <p className="text-sm text-blue-500 mt-1">Expira: {new Date(success.trial_expires_at).toLocaleDateString('es')}</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
            <p className="font-bold text-gray-700 mb-2">Datos de ejemplo incluidos:</p>
            <p className="text-sm text-gray-600">{success.datos_ejemplo?.clientes_creados} clientes de prueba</p>
            <p className="text-sm text-gray-600">{success.datos_ejemplo?.envios_creados} envios en diferentes estados</p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-left">
            <p className="font-bold text-yellow-800 mb-1">Sus credenciales:</p>
            <p className="text-sm text-yellow-700">Email: <span className="font-mono font-bold">{success.email}</span></p>
            <p className="text-sm text-yellow-700">Password: la que usted eligio</p>
          </div>

          <Button
            data-testid="go-login-btn"
            className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
            onClick={() => navigate('/login')}
          >
            Iniciar Sesion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      {/* Header */}
      <div className="p-4">
        <Button
          variant="ghost"
          className="text-white hover:bg-white/10"
          onClick={() => navigate('/login')}
          data-testid="back-to-login-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Volver
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-8 px-4 pb-12">
        {/* Left side - Benefits */}
        <div className="max-w-md text-white">
          {/* Hero Section */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Package className="h-8 w-8 text-yellow-400" />
              <span className="text-yellow-400 font-bold text-lg">DEMO GRATUITO</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">Sistema de Envios</h1>
            <h2 className="text-xl text-blue-300 mb-4">USA → Republica Dominicana</h2>
            <p className="text-gray-300 leading-relaxed">
              Software completo para empresas de envios y carga. Gestione clientes, facturas, 
              recogidas, furgones, rastreo de paquetes y mas. Todo en un solo lugar.
            </p>
          </div>

          {/* What's included */}
          <div className="bg-white/5 rounded-2xl p-4 border border-white/10 mb-6">
            <p className="font-bold text-yellow-400 mb-3 flex items-center gap-2">
              <Rocket className="h-5 w-5" /> 15 dias de prueba incluyen:
            </p>
            <div className="space-y-3">
              {[
                { icon: Package, text: 'Gestion completa de envios', sub: 'Facturas, etiquetas, manifiestos, rastreo' },
                { icon: Shield, text: 'Su propia cuenta privada', sub: 'Datos 100% separados de otras empresas' },
                { icon: CheckCircle, text: 'Datos de ejemplo cargados', sub: 'Clientes, envios y facturas para probar' },
                { icon: Clock, text: 'Sin compromiso', sub: 'Cancele cuando quiera, sin cargos' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <item.icon className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">{item.text}</p>
                    <p className="text-xs text-blue-300">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact info */}
          <div className="text-sm text-gray-400">
            <p>Preguntas? Contactenos:</p>
            <p className="text-white font-semibold">718-916-1401 | lametaforashipping@gmail.com</p>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-6 sm:p-8" data-testid="trial-register-form">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Crear Cuenta de Prueba</h2>
          <p className="text-gray-500 mb-6">Complete los datos de su empresa</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="font-semibold flex items-center gap-2"><Building2 className="h-4 w-4" /> Nombre de la Empresa *</Label>
              <Input
                data-testid="trial-empresa-input"
                placeholder="Ej: MI SHIPPING LLC"
                value={form.empresa_nombre}
                onChange={(e) => setForm({ ...form, empresa_nombre: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Su Nombre Completo *</Label>
              <Input
                data-testid="trial-nombre-input"
                placeholder="Ej: Juan Perez"
                value={form.contacto_nombre}
                onChange={(e) => setForm({ ...form, contacto_nombre: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="font-semibold flex items-center gap-2"><Mail className="h-4 w-4" /> Email *</Label>
              <Input
                data-testid="trial-email-input"
                type="email"
                placeholder="Ej: admin@miempresa.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="font-semibold flex items-center gap-2"><Lock className="h-4 w-4" /> Contrasena *</Label>
              <Input
                data-testid="trial-password-input"
                type="password"
                placeholder="Minimo 6 caracteres"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="font-semibold flex items-center gap-2"><Phone className="h-4 w-4" /> Telefono *</Label>
              <Input
                data-testid="trial-telefono-input"
                placeholder="Ej: 347-555-1234"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label className="font-semibold flex items-center gap-2"><Tag className="h-4 w-4" /> Prefijo de Factura</Label>
              <Input
                data-testid="trial-prefijo-input"
                placeholder="Ej: MS, DM, FMS (2-4 letras)"
                value={form.prefijo_factura}
                onChange={(e) => setForm({ ...form, prefijo_factura: e.target.value.toUpperCase().slice(0, 4) })}
                maxLength={4}
                className="mt-1"
              />
              <p className="text-xs text-gray-400 mt-1">Las iniciales de su empresa para las facturas</p>
            </div>

            <Button
              type="submit"
              data-testid="trial-submit-btn"
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6 mt-2"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  Creando empresa...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Rocket className="h-5 w-5" /> Comenzar Prueba Gratuita
                </span>
              )}
            </Button>

            <p className="text-center text-xs text-gray-400 mt-2">
              Al registrarse acepta los terminos de uso. Demo provisto por Global Metafora App.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrialRegister;
